import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/shared/ui/atoms';
import { useBoard } from '@/features/project-board/BoardProvider';
import { DetailDrawer, type DrawerPayload } from '@/features/project-board/components/DetailDrawer';
import { DrawerPayloads } from '@/features/project-board/drawer-payloads';
import { FilterBar } from '@/features/project-board/components/FilterBar';
import { SourceTabs } from '@/features/project-board/components/SourceTabs';

/**
 * 화면 공통 껍데기 — 필터 바 · 로딩/에러 · 상세 드로어를 한 번만 구현한다.
 * 화면은 `render(open)`으로 본문만 그리고, 항목을 열 때 open(payload)을 부른다.
 *
 * 드로어가 지금 보여주는 항목·문서는 주소창 쿼리(`item`/`kind`/`doc`)에도 그대로 반영한다 — 그 주소를
 * 복사해 붙여넣으면 같은 항목·같은 문서가 열린 채로 페이지가 뜬다. 반대로 그 쿼리를 달고 들어오면
 * 색인이 준비되는 대로 한 번 복원한다(그 뒤로는 사용자 조작이 정본이라 다시 읽지 않는다).
 */
export function ViewFrame({
  scope,
  countLabel,
  isEmpty,
  emptyMessage,
  toolbar,
  render,
}: {
  scope: 'tasks' | 'issues' | 'incidents' | 'epics' | 'qa';
  countLabel: string;
  isEmpty: boolean;
  emptyMessage: string;
  toolbar?: React.ReactNode;
  render: (open: (payload: DrawerPayload) => void) => React.ReactNode;
}): React.JSX.Element {
  const { loading, error, index } = useBoard();
  const [payload, setPayload] = useState<DrawerPayload | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const restoredFromLink = useRef(false);
  const initialDocPath = useRef(searchParams.get('doc')).current;

  useEffect(() => {
    if (restoredFromLink.current || !index) return;
    restoredFromLink.current = true;
    const kind = searchParams.get('kind');
    const item = searchParams.get('item');
    if (!kind || !item) return;
    const resolved = DrawerPayloads.resolveLinked(index, kind, item);
    if (resolved) setPayload(resolved);
  }, [index]);

  const open = useCallback((next: DrawerPayload) => setPayload(next), []);

  const close = useCallback(() => {
    setPayload(null);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('item');
        next.delete('kind');
        next.delete('doc');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const onSelectionChange = useCallback(
    // 드로어가 닫힌 상태(selection === null)는 여기서 다루지 않는다 — `close()`가 그 경우를 맡는다.
    // 여기서도 지우면, 색인이 아직 안 실려 복원 전인 첫 렌더에도 (열린 게 없으니) null이 들어와
    // 막 도착한 공유 링크의 쿼리를 복원하기도 전에 지워 버린다.
    (selection: { kind: string; key: string; docPath: string | null } | null) => {
      if (!selection) return;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('item', selection.key);
          next.set('kind', selection.kind);
          if (selection.docPath) next.set('doc', selection.docPath);
          else next.delete('doc');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <>
      <SourceTabs />
      <FilterBar scope={scope} countLabel={countLabel} toolbar={toolbar} />

      {error && <EmptyState message={`불러오지 못했다 — ${error}`} />}
      {!error && !index && loading && <EmptyState message="색인을 불러오는 중…" />}
      {!error && index && isEmpty && <EmptyState message={emptyMessage} />}
      {!error && index && !isEmpty && render(open)}

      <DetailDrawer
        payload={payload}
        onClose={close}
        initialDocPath={initialDocPath}
        onSelectionChange={onSelectionChange}
      />
    </>
  );
}
